const API_BASE_URL = '/api/bt';

export interface ChurchManager {
  _id: string;
  churchName: string;
  churchNameEn?: string;
  metadata?: {
    churchId?: string;
    mainId?: string;
    subId?: string;
  };
  churchAddress: string;
  churchPhone: string;
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  participants?: number;
  selectedSessionId?: string;
  selectedSessionTitle?: string;
  selectedSessionProgram?: string;
  teachers?: Array<{
    id: number;
    key: string;
    name: string;
    teacherNameEn?: string;
    phone: string;
    lectureSessionId?: string;
    lectureSessionTitle?: string;
    lectureSessionProgram?: string;
    status?: 'active' | 'pending_add' | 'pending_delete' | 'deleted';
    isNew?: boolean;
    isDeleted?: boolean;
  }>;
  status: 'pending' | 'approved' | 'rejected';
  registrationDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface IndividualTeacher {
  teacherNameEn?: string;
  _id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  churchName?: string;
  position?: string;
  experience?: string;
  certification?: string;
  motivation: string;
  status: 'pending' | 'approved' | 'rejected';
  registrationDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface BTReceipt {
  _id: string;
  eventId: string;
  churchId: {
    mainId: string;
    subId: string;
  };
  churchName: string;
  managerName: string;
  managerPhone: string;
  partTotal: number;
  partStudent: number;
  partTeacher: number;
  partYM: number;
  costs: number;
  paymentMethod: 'card' | 'bank' | 'cash';
  paymentStatus: 'pending' | 'completed' | 'cancelled';
  paymentDate?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Church {
  _id: string;
  mainId: string;
  subId: string;
  name: string;
  location: string;
  createdAt: string;
  updatedAt: string;
}

export interface BTAttendanceRecord {
  _id: string;
  name?: string;
  teacher_phone?: string;
  church_name?: string;
  church_id?: string;
  bt_club_id?: string;
  bt_club_name?: string;
  bt_location_id?: string;
  bt_location_name?: string;
  education_type?: 'new' | 'renewal' | 'advanced';
  session_id?: string;
  sessionId?: string;
  sessionType?: string;
  checkInTime?: string;
  status?: string;
  method?: string;
  eventId?: string;
  teacherId?: string | {
    teacherName?: string;
    teacherPhone?: string;
    churchName?: string;
  };
  location?: string;
  keyCode?: string;
  session?: Pick<BTSession, 'sessionId' | 'title' | 'trainingCategory' | 'leadershipTrack' | 'educationProgram' | 'location' | 'btClubTrack' | 'btClubTracks' | 'basicTrainingLevel'> | null;
  teacher_key?: string;
  start_time?: string;
  createdAt: string;
}

export interface BTAttendanceUpdatePayload {
  name?: string;
  teacher_phone?: string;
  church_name?: string;
  bt_club_id?: string;
  bt_location_id?: string;
  education_type?: 'new' | 'renewal' | 'advanced';
}

export interface BTLocation {
  _id: string;
  location_name: string;
  isDeleted?: boolean;
}

export interface BTClub {
  _id: string;
  bt_club_name?: string;
  club_name?: string;
}

export type LeadershipEducationProgram =
  | 'bt-new'
  | 'bt-renewal'
  | 'bt-advanced'
  | 'supervisor-school'
  | 'coordinator-school'
  | 'lcs';

export type SessionTrainingCategory = 'basic-training' | 'leadership-education';
export type BasicTrainingType = 'regular' | 'united' | 'open-bt';
export type BasicTrainingLevel = 'new' | 'renewal' | 'advanced';
export type BTClubTrack = 'CUBBIES' | 'JOURNEY' | 'PUGGLES' | 'SPARKS' | 'T&T' | 'TREK' | 'SECRETARY' | 'COMMON';
export type LeadershipTrack = 'supervisor-school' | 'coordinator-school' | 'lcs';

export interface BTSession {
  _id: string;
  sessionId: string;
  title: string;
  description: string;
  trainingCategory?: SessionTrainingCategory;
  basicTrainingType?: BasicTrainingType;
  basicTrainingLevel?: BasicTrainingLevel;
  btClubTrack?: BTClubTrack;
  btClubTracks?: BTClubTrack[];
  leadershipTrack?: LeadershipTrack;
  educationProgram?: LeadershipEducationProgram;
  registrationStartDate?: string;
  registrationEndDate?: string;
  sessionType: 'morning' | 'afternoon' | 'evening' | 'full-day';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  currentParticipants: number;
  uniqueGroupParticipants?: number;
  status: 'scheduled' | 'active' | 'completed' | 'cancelled';
  eventId: string;
  instructors?: string[];
  materials?: string[];
  requirements?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BTKey {
  _id: string;
  keyCode: string;
  churchManagerId: string;
  eventId: string;
  keyType: 'church' | 'individual';
  status: 'available' | 'assigned' | 'used' | 'expired';
  assignedTeacherId?: string;
  assignedTeacher?: BTTeacher;
  assignedDate?: string;
  usedDate?: string;
  qrCode?: string;
  qrGeneratedAt?: string;
  expiresAt: string;
  metadata?: {
    churchName?: string;
    churchId?: string;
    managerPhone?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface BTTeacher {
  _id: string;
  teacherName: string;
  teacherNameEn?: string;
  teacherPhone: string;
  teacherEmail?: string;
  churchManagerId?: string | null | {
    _id: string;
    churchName: string;
    managerName: string;
  };
  churchName?: string;
  position?: string;
  positionType?: string;
  awanaRole?: string;
  club?: string;
  experience?: string;
  specialNotes?: string;
  keyCode?: string;
  eventId?: string;
  motivation?: string;
  qrCode?: string;
  attendedLectures?: AttendedLectureSummary[];
  photoPath?: string;
  photoMimeType?: 'image/jpeg' | 'image/png' | 'image/webp';
  idCardEnabled?: boolean;
  idCardIssuedAt?: string;
  status: 'pending' | 'approved' | 'rejected' | 'registered' | 'active' | 'cancelled' | 'expired';
  registrationDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttendedLectureSummary {
  sessionId?: string;
  title: string;
  attendedAt: string;
  location?: string;
}

export interface TeacherIdCard {
  _id: string;
  keyCode: string;
  teacherName: string;
  churchName: string;
  position: string;
  status: BTTeacher['status'];
  idCardIssuedAt?: string;
  photoUrl: string;
  attendedLectures?: AttendedLectureSummary[];
}

export interface TeacherIdQr {
  token: string;
  expiresAt: string;
}

class BTApi {
  async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : { message: await response.text() };

      if (!response.ok) {
        const message = data.error || data.message;
        const isInfrastructureError = response.status >= 500 || /proxy|gateway/i.test(message || '');
        throw new Error(
          !isInfrastructureError && message && message.length < 200
            ? message
            : `서비스에 연결할 수 없습니다. (${response.status})`
        );
      }

      return data;
    } catch (error) {
      console.error('BT API request failed:', error);
      throw error;
    }
  }

  // Church Manager APIs
  async createChurchManager(churchData: Partial<ChurchManager>): Promise<ChurchManager> {
    const response = await this.request('/church-managers', {
      method: 'POST',
      body: JSON.stringify(churchData),
    });
    return response.data;
  }

  async getChurchManagers(params: any = {}): Promise<{
    data: ChurchManager[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const queryString = new URLSearchParams(params).toString();
    const response = await this.request(`/church-managers?${queryString}`);
    return response;
  }

  async getChurchManagerById(id: string): Promise<ChurchManager> {
    const response = await this.request(`/church-managers/${id}`);
    return response.data;
  }

  async updateChurchManagerStatus(
    id: string,
    status: string,
    eventId?: string,
    costs?: number,
    partTeacher?: number
  ): Promise<ChurchManager> {
    const body: any = { status };
    if (eventId) body.eventId = eventId;
    if (costs) body.costs = costs;
    if (partTeacher) body.partTeacher = partTeacher;

    const response = await this.request(`/church-managers/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.data;
  }

  async deleteChurchManager(id: string): Promise<void> {
    await this.request(`/church-managers/${id}`, {
      method: 'DELETE',
    });
  }

  // Individual Teacher APIs
  async createIndividualTeacher(teacherData: Partial<IndividualTeacher>): Promise<IndividualTeacher> {
    const response = await this.request('/individual-teachers', {
      method: 'POST',
      body: JSON.stringify(teacherData),
    });
    return response.data;
  }

  async getIndividualTeachers(params: any = {}): Promise<{
    data: IndividualTeacher[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const queryString = new URLSearchParams(params).toString();
    const response = await this.request(`/individual-teachers?${queryString}`);
    return response;
  }

  async getIndividualTeacherById(id: string): Promise<IndividualTeacher> {
    const response = await this.request(`/individual-teachers/${id}`);
    return response.data;
  }

  async updateIndividualTeacherStatus(id: string, status: string): Promise<IndividualTeacher> {
    const response = await this.request(`/individual-teachers/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return response.data;
  }

  // BT Receipt APIs
  async getBTReceipts(params: any = {}): Promise<{
    data: BTReceipt[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const queryString = new URLSearchParams(params).toString();
    const response = await this.request(`/receipts?${queryString}`);
    return response;
  }

  async getBTReceiptById(id: string): Promise<BTReceipt> {
    const response = await this.request(`/receipts/${id}`);
    return response.data;
  }

  async getBTReceiptByChurchManager(churchManagerId: string): Promise<BTReceipt[]> {
    const response = await this.request(`/church-managers/${churchManagerId}/receipts`);
    return response.data;
  }

  // Church search
  async searchChurches(query: string): Promise<Church[]> {
    const response = await this.request(`/churches/search?query=${encodeURIComponent(query)}`);
    return response.data;
  }

  // Statistics
  async getBTStatistics(): Promise<any> {
    const response = await this.request('/statistics');
    return response.data;
  }

  // ===== BT 출석 관리 API =====

  // 출석 목록 조회
  async getAttendanceList(params?: Record<string, string>): Promise<{
    data: BTAttendanceRecord[];
    meta?: { page: number; size: number; totalCount: number; totalPage: number };
  }> {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await this.request(`/bt_attendance${queryString ? '?' + queryString : ''}`);
    return response;
  }

  // 수강지역 목록 조회
  async getLocations(params?: Record<string, string>): Promise<BTLocation[]> {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await this.request(`/bt_location${queryString ? '?' + queryString : ''}`);
    return response;
  }

  // 클럽 목록 조회
  async getClubs(): Promise<BTClub[]> {
    const response = await this.request('/bt_club');
    return response;
  }

  // 출석 기록 수정
  async updateAttendance(id: string, payload: BTAttendanceUpdatePayload): Promise<BTAttendanceRecord> {
    const response = await this.request(`/bt_attendance/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return response.data;
  }

  // 출석 삭제
  async deleteAttendance(id: string): Promise<void> {
    await this.request(`/bt_attendance?id=${id}`, { method: 'DELETE' });
  }

  // ===== BT 세션 관리 API =====

  // 세션 목록 조회
  async getSessions(params?: Record<string, string>): Promise<{ data: BTSession[], pagination?: any }> {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await this.request(`/sessions${queryString ? '?' + queryString : ''}`);
    return response;
  }

  // 세션 단일 조회 (MongoDB _id)
  async getSessionById(id: string): Promise<BTSession> {
    const response = await this.request(`/sessions/${id}`);
    return response.data;
  }

  // 세션 조회 (sessionId로)
  async getSessionBySessionId(sessionId: string): Promise<BTSession> {
    const response = await this.request(`/sessions/session/${sessionId}`);
    return response.data;
  }

  // 세션 생성
  async createSession(sessionData: Partial<BTSession>): Promise<BTSession> {
    const response = await this.request('/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
    return response.data;
  }

  // 세션 수정
  async updateSession(id: string, sessionData: Partial<BTSession>): Promise<BTSession> {
    const response = await this.request(`/sessions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData),
    });
    return response.data;
  }

  // 세션 삭제
  async deleteSession(id: string): Promise<void> {
    await this.request(`/sessions/${id}`, { method: 'DELETE' });
  }

  // 세션 상태 변경
  async updateSessionStatus(id: string, status: string): Promise<BTSession> {
    const response = await this.request(`/sessions/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return response.data;
  }

  // 세션 출석 통계
  async getSessionAttendanceStats(sessionId: string): Promise<any> {
    const response = await this.request(`/sessions/session/${sessionId}/attendance`);
    return response.data;
  }

  async getSessionAttendanceRecords(sessionId: string): Promise<any[]> {
    const response = await this.request(`/sessions/session/${sessionId}/attendance/records`);
    return response.data;
  }

  // ===== BT 키 관리 API =====

  // 교회별 키 목록 조회
  async getKeysByChurchManager(churchManagerId: string): Promise<{ data: BTKey[] }> {
    const response = await this.request(`/keys/church-manager/${churchManagerId}`);
    return response;
  }

  // 키 생성
  async generateKeys(churchManagerId: string, eventId: string, keyCount: number): Promise<{ data: BTKey[] }> {
    const response = await this.request('/keys/generate', {
      method: 'POST',
      body: JSON.stringify({ churchManagerId, eventId, keyCount }),
    });
    return response;
  }

  // 키를 교사에게 할당
  async assignKeyToTeacher(keyCode: string, teacherId: string): Promise<BTKey> {
    const response = await this.request('/keys/assign', {
      method: 'POST',
      body: JSON.stringify({ keyCode, teacherId }),
    });
    return response.data;
  }

  // 키 사용 (QR 코드 생성)
  async useKey(keyCode: string): Promise<BTKey> {
    const response = await this.request('/keys/use', {
      method: 'POST',
      body: JSON.stringify({ keyCode }),
    });
    return response.data;
  }

  // ===== BT 교사 관리 API =====

  // 교회별 교사 목록 조회
  async getTeachersByChurchManager(churchManagerId: string): Promise<{ data: BTTeacher[] }> {
    const response = await this.request(`/church-managers/${churchManagerId}/teachers`);
    return response;
  }

  // 교사 저장 (새 교사 추가 또는 업데이트)
  async saveTeachers(churchManagerId: string, teachers: Partial<BTTeacher>[]): Promise<{ data: BTTeacher[] }> {
    const response = await this.request(`/church-managers/${churchManagerId}/teachers`, {
      method: 'POST',
      body: JSON.stringify({ teachers }),
    });
    return response;
  }

  // BTTeacher 직접 생성
  async createBTTeacher(teacherData: Partial<BTTeacher>): Promise<BTTeacher> {
    const baseYear = 2026;
    const mainIdRaw = String(
      (teacherData as any)?.churchId?.mainId
      || (teacherData as any)?.churchRegistrationNumber
      || '0000'
    );

    const keyPrefix = String(baseYear);
    const churchRegistrationNumber = mainIdRaw.replace(/\D/g, '').padStart(4, '0').slice(-4);
    const teacherSequence = String(Math.floor(1 + Math.random() * 999)).padStart(3, '0');
    const keyCode = teacherData.keyCode || `${keyPrefix}-${churchRegistrationNumber}-${teacherSequence}`;
    const payload = {
      ...teacherData,
      keyCode,
      eventId: teacherData.eventId || `leadership-education-${baseYear}`,
      motivation: teacherData.motivation || '관리자 등록',
      qrCode: teacherData.qrCode || `BT-TEACHER:${keyCode}`,
    };

    const response = await this.request('/teachers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.data;
  }

  async updateBTTeacher(id: string, teacherData: Partial<BTTeacher>): Promise<BTTeacher> {
    const response = await this.request(`/teachers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(teacherData),
    });
    return response.data;
  }

  async updateTeacherIdCardStatus(id: string, enabled: boolean): Promise<BTTeacher> {
    const response = await this.request(`/teachers/${id}/id-card`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
    return response.data;
  }

  async uploadTeacherIdPhoto(id: string, file: File): Promise<BTTeacher> {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await fetch(`${API_BASE_URL}/teachers/${id}/id-card/photo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : { message: await response.text() };
    if (!response.ok) {
      const message = data.error || data.message;
      throw new Error(
        response.status < 500 && message && message.length < 200
          ? message
          : `서비스에 연결할 수 없습니다. (${response.status})`
      );
    }
    return data.data;
  }

  async loginTeacherId(credentials: {
    churchName?: string;
    teacherName?: string;
    keyCode?: string;
    teacherEmail?: string;
    teacherPhone: string;
  }): Promise<TeacherIdCard> {
    const response = await this.request('/teacher-id/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    return response.data;
  }

  async logoutTeacherId(): Promise<void> {
    await this.request('/teacher-id/logout', { method: 'POST' });
  }

  async getMyTeacherId(): Promise<TeacherIdCard> {
    const response = await this.request('/teacher-id/me');
    return response.data;
  }

  async getTeacherIdQr(): Promise<TeacherIdQr> {
    const response = await this.request('/teacher-id/qr');
    return response.data;
  }

  async getTeacherIdChurchSuggestions(query: string): Promise<string[]> {
    const response = await this.request(`/teacher-id/churches?query=${encodeURIComponent(query)}`);
    return response.data || [];
  }

  async verifyTeacherId(token: string): Promise<TeacherIdCard> {
    const response = await this.request(`/teacher-id/verify?token=${encodeURIComponent(token)}`);
    return response.data;
  }

  // BTTeacher 전체 목록 조회
  async getBTTeachers(params?: Record<string, string>): Promise<{ data: BTTeacher[] }> {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await this.request(`/teachers${queryString ? '?' + queryString : ''}`);
    return response;
  }

  // ===== 교사 변경 요청 API =====

  // 교사 변경 요청 목록 조회
  async getTeacherChangeRequests(params?: Record<string, string>): Promise<{ data: TeacherChangeRequest[], pagination: any }> {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    const response = await this.request(`/teacher-change-requests${queryString ? '?' + queryString : ''}`);
    return response;
  }

  // 교사 변경 요청 승인/거절
  async updateTeacherChangeRequestStatus(id: string, status: 'approved' | 'rejected', rejectionReason?: string): Promise<any> {
    const body: any = { status };
    if (rejectionReason) body.rejectionReason = rejectionReason;

    const response = await this.request(`/teacher-change-requests/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response;
  }
}

// 교사 변경 요청 인터페이스
export interface TeacherChangeRequest {
  _id: string;
  churchManagerId: string | { churchName: string; managerPhone: string };
  requestType: 'add' | 'delete' | 'modify';
  teacherData: {
    id?: number;
    key?: string;
    name: string;
    phone: string;
  };
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  requestDate: string;
  processedDate?: string;
}

export const btApi = new BTApi();
export default btApi;
